import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { OFFICIAL_LOGO, EVENT_TAGLINE } from "@/lib/brand";

/**
 * Official logo reveal — a dedicated section showcasing the championship's
 * official emblem with subtle animations and the tagline.
 */
export default function LogoBanner() {
  const sectionRef = useRef(null);
  // These orbs are large (up to 40rem) with heavy blur — expensive to keep
  // compositing while scrolled out of view, so only animate while visible.
  const inView = useInView(sectionRef, { margin: "-10% 0px -10% 0px" });

  return (
    <section
      ref={sectionRef}
      data-testid="logo-banner"
      className="relative py-16 md:py-24 bg-gradient-to-b from-[#0B1429] via-[#142040] to-[#0B1429] overflow-hidden"
    >
      {/* Aurora orbs — single aqua accent, per The One Glow Rule */}
      <motion.div
        className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none"
        animate={inView ? { scale: [1, 1.1, 1], opacity: [0.08, 0.16, 0.08] } : { opacity: 0 }}
        transition={inView ? { duration: 6, repeat: Infinity, ease: "easeInOut" } : { duration: 0.4 }}
        style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)", filter: "blur(80px)" }}
      />
      <motion.div
        className="absolute -right-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none"
        animate={inView ? { scale: [1.1, 1, 1.1], opacity: [0.08, 0.16, 0.08] } : { opacity: 0 }}
        transition={inView ? { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 } : { duration: 0.4 }}
        style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)", filter: "blur(80px)" }}
      />
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full pointer-events-none"
        animate={inView ? { scale: [1, 1.08, 1], opacity: [0.08, 0.18, 0.08] } : { opacity: 0 }}
        transition={inView ? { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 } : { duration: 0.4 }}
        style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)", filter: "blur(120px)" }}
      />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-[0_30px_120px_rgba(0,240,255,0.25)] relative"
        >
          {/* Soft glow ring — aqua only, per The One Glow Rule */}
          <motion.div
            className="absolute -inset-1 rounded-2xl pointer-events-none"
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background: "#00F0FF",
              filter: "blur(12px)",
              zIndex: -1,
            }}
          />
          <img
            src={OFFICIAL_LOGO}
            alt="2nd Asian Masters Swimming Championships 2026 — Bengaluru, India"
            className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 object-contain"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-8 max-w-xl"
        >
          <div className="font-display font-black text-xl sm:text-3xl md:text-4xl tracking-tight px-2 text-white">
            "{EVENT_TAGLINE}"
          </div>
          <div className="text-xs uppercase tracking-[0.35em] text-[#94A3B8] mt-4 font-semibold">
            The Official Motto
          </div>
        </motion.div>
      </div>
    </section>
  );
}
